import { ChevronDownIcon, ChevronUpIcon } from '@heroicons/react/20/solid';
import { animated, useSpring } from '@react-spring/web';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import clsx from 'clsx';
import CustomerPreferencesSection from 'components/layout/customer-preferences-section';
import SendPhotoCollectorModal from 'components/project-page/modals/send-photo-collector-modal';
import { toggleSettingsPanel } from 'features/ui';
import useHidesCustomerPriorities from 'hooks/use-hides-customer-priorities';
import { useState } from 'react';

type SettingsPanelProps = {
  loading: boolean;
};

// The collapsed-by-default side panel. On the ductless and hybrid pathways it
// holds the Customer Priorities sliders (which score the ductless gallery)
// and the Customer Preferences questionnaire; on the compatibility (ducted)
// and PTAC pathways the Choose Systems grid is ordered without the sliders,
// so the panel holds the questionnaire alone and its toggle says so.
export default function SettingsPanel({ loading }: SettingsPanelProps) {
  const dispatch = useAppDispatch();
  const { showSettingsPanel } = useAppSelector((state) => state.ui);
  const hidesCustomerPriorities = useHidesCustomerPriorities();
  const [photoCollectorOpen, setPhotoCollectorOpen] = useState(false);
  const spring = useSpring({
    width: showSettingsPanel ? '320px' : '0px',
    config: { tension: 300, friction: 30 }
  });

  const buttonClasses = clsx(
    'mt-5 flex h-[240px] w-10 items-center justify-center rounded-r-lg bg-white hover:bg-gray-50 z-20',
    loading ? 'opacity-[70%] cursor-not-allowed' : 'cursor-pointer'
  );
  const panelName = hidesCustomerPriorities ? 'Preferences' : 'Priorities';

  return (
    <div className='flex'>
      <animated.div
        className='relative h-full overflow-hidden border-r border-gray-200 bg-white'
        style={spring}
      >
        <div className='absolute h-full w-[320px] overflow-y-auto p-5'>
          <div className='flex flex-col gap-4'>
            <CustomerPreferencesSection />
          </div>
        </div>
      </animated.div>
      <button
        type='button'
        className={buttonClasses}
        onClick={() => dispatch(toggleSettingsPanel())}
        disabled={loading}
      >
        <div className='flex -rotate-90 items-center gap-1 text-gray-500'>
          <span className='whitespace-nowrap text-sm font-semibold'>
            {showSettingsPanel ? `Hide ${panelName}` : `Show ${panelName}`}
          </span>
          {showSettingsPanel ? (
            <ChevronUpIcon className='h-6 w-6' />
          ) : (
            <ChevronDownIcon className='h-6 w-6' />
          )}
        </div>
      </button>
      <SendPhotoCollectorModal
        openState={[photoCollectorOpen, setPhotoCollectorOpen]}
      />
    </div>
  );
}
